import type { Message } from "@langchain/langgraph-sdk";
import {
  Chat,
  useChatMessages,
  useChatIsStreaming,
  useChatInterrupt,
  useChatRespondToInterrupt,
  useAgentActivity,
  extractText,
  InterruptCard,
} from "@launch10/use-langgraph-stream";
import { Fragment } from "react";
import type {
  HumanInterrupt,
  HumanResponse,
} from "@launch10/use-langgraph-stream/humanInterrupt";
import { ThinkingIndicator } from "@components/shared/chat/ThinkingIndicator";
import { CompactingIndicator } from "@components/shared/chat/CompactingIndicator";
import { ChatErrorBanner } from "@components/shared/chat/ChatErrorBanner";
import { useWebsiteChatState, useWebsiteChatIsCompacting } from "@hooks/website";
import type { Todo } from "@shared";

/**
 * Inline-renders the active HumanInterrupt as a card in the chat stream.
 * Returns null when the graph isn't paused on an interrupt. The
 * editCustomField rule already inlines the affected sites/pages into the
 * description prose, so we just render the standard parts (no
 * domain-specific body); the generic Args dump is suppressed for that
 * tool since the description carries the same information.
 */
function ActiveInterruptCard() {
  const interrupt = useChatInterrupt<HumanInterrupt>();
  const respond = useChatRespondToInterrupt<HumanResponse>();
  if (!interrupt) return null;

  const toolName = String(interrupt.action_request?.args?.toolName ?? "");
  const isFileEdit = toolName === "edit_file" || toolName === "delete_file";

  return (
    <InterruptCard interrupt={interrupt} onResponse={respond} className="text-xs">
      <InterruptCard.Header className="text-sm" />
      <InterruptCard.Description className="text-xs" />
      {/* For file-edit interrupts the description already names the
          affected sites/pages inline — no separate body needed. Future
          interrupt kinds with structured args can fall back to the
          generic <InterruptCard.Args /> renderer. */}
      {!isFileEdit && <InterruptCard.Args />}
      <InterruptCard.Actions>
        <InterruptCard.Approve />
        <InterruptCard.Reject />
        <InterruptCard.Respond />
      </InterruptCard.Actions>
    </InterruptCard>
  );
}

/**
 * Props for the WebsiteChatMessagesView presentation component.
 */
export interface WebsiteChatMessagesViewProps {
  messages: Message[];
  isStreaming: boolean;
  /** True while the agent is compacting history — shows CompactingIndicator. */
  isCompacting?: boolean;
  todos?: Todo[];
}

/**
 * Pure presentation component for website chat messages.
 * Uses Chat compound components for consistent styling.
 *
 * Human messages route through Recommendations.SubmissionMessage — a
 * submitted recommendation panel renders as its structured card (the
 * transcript anchor), everything else falls back to the normal bubble.
 * The recommendation chips row itself lives in the input area
 * (WebsiteChat), not the transcript.
 */
export function WebsiteChatMessagesView({
  messages,
  isStreaming,
  isCompacting = false,
  todos,
}: WebsiteChatMessagesViewProps) {
  // The agent's live work: tool-activity bursts joined to the todo
  // checklist. The hook folds in the todo→row completion sync,
  // delegation extraction, and the current-turn burst. Stays above the
  // empty-state returns.
  const { byMessage, currentGroupId, currentGroup, delegations, isLive } =
    useAgentActivity({ messages, todos, isStreaming });

  if (messages.length === 0 && isStreaming) {
    return <ThinkingIndicator text="Building your website" className="text-xs" />;
  }

  if (messages.length === 0) {
    return <ThinkingIndicator text="Getting ready" className="text-xs" />;
  }

  const hasCurrentWork = Boolean(currentGroup) || (todos?.length ?? 0) > 0;

  // Track whether an AI header ("Houston") has rendered since the last
  // human message — follow-on AI messages continue the same lockup so a
  // multi-beat turn reads as one conversational pass.
  let aiHeaderShown = false;

  return (
    <Chat.Messages.List className="flex-1 min-h-0" scrollTriggers={todos?.map((todo) => todo.content)}>
      {messages.map((message, index) => {
        const isLastMessage = index === messages.length - 1;

        if (message.type === "human" || (message as any).role === "user") {
          aiHeaderShown = false;
          return (
            <Chat.Recommendations.SubmissionMessage
              key={message.id}
              message={message}
              fallback={<Chat.UserMessage message={message} className="text-xs" />}
            />
          );
        }

        if (message.type === "ai" || (message as any).role === "assistant") {
          const text = extractText(message.content);
          // The current-turn burst hoists into the trailing AgentActivity
          // card (with the todos), so it's excluded here; settled bursts
          // from earlier beats stay inline as tally strips.
          const activity = message.id ? byMessage.get(message.id) : undefined;
          const inlineActivity =
            activity && activity.anchorMessageId !== currentGroupId
              ? activity
              : undefined;
          if (!text.trim() && !inlineActivity) return null;

          const continuation = aiHeaderShown;
          if (text.trim()) aiHeaderShown = true;

          return (
            <Fragment key={message.id}>
              {text.trim() && (
                <Chat.AIMessage.Root continuation={continuation}>
                  <Chat.AIMessage.Content
                    content={message.content}
                    state={isLastMessage ? "active" : "inactive"}
                    className="text-xs"
                  />
                </Chat.AIMessage.Root>
              )}
              {inlineActivity && (
                <Chat.ToolActivity
                  group={inlineActivity}
                  isStreaming={isStreaming}
                  className="text-xs"
                />
              )}
            </Fragment>
          );
        }

        return null;
      })}
      <Chat.QueuedMessages />
      {/* The cohesion card: the current-turn tool feed and the todo
          checklist as one unit, so the "how" sits with the "what". */}
      {isStreaming && hasCurrentWork && (
        <Chat.AgentActivity
          group={currentGroup}
          todos={todos}
          delegations={delegations}
          isStreaming={isStreaming}
          className="text-xs"
        />
      )}
      {isCompacting ? (
        <CompactingIndicator className="text-xs" />
      ) : (
        // While a burst is live the card carries its own spinner — the
        // trailing "Thinking" would be a second, redundant pulse.
        isStreaming && !isLive && (
          <ThinkingIndicator text="Thinking" className="text-xs" />
        )
      )}
      {/*
        Inline-render the active interrupt (if any). Sits below the message
        list and above the error banner so the card is the most recent thing
        the user sees when the graph pauses. Renders nothing when there's
        no active interrupt.
      */}
      <ActiveInterruptCard />
      <ChatErrorBanner />
    </Chat.Messages.List>
  );
}

/**
 * Container component for website chat messages.
 */
export default function WebsiteChatMessages() {
  const messages = useChatMessages();
  const isStreaming = useChatIsStreaming();
  const isCompacting = useWebsiteChatIsCompacting();
  const todos = useWebsiteChatState("todos");

  return (
    <WebsiteChatMessagesView
      messages={messages as Message[]}
      isStreaming={isStreaming}
      isCompacting={isCompacting}
      todos={todos as Todo[] | undefined}
    />
  );
}
